import { openDB } from "idb";
import { api, API } from "@/lib/api";

const DB_NAME = "deca-offline";
const DB_VERSION = 1;

async function getDb() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("decas")) db.createObjectStore("decas", { keyPath: "id" });
      if (!db.objectStoreNames.contains("pdfs")) db.createObjectStore("pdfs", { keyPath: "id" });
      if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta", { keyPath: "key" });
    },
  });
}

/** El QR público ya no sirve (L6 caducado o DeCA anulado). */
export function isPublicDownloadActive(d) {
  if (!d || d.estado === "anulado" || d.estado === "borrador") return false;
  const until = d.download_disabled_at;
  if (!until) return true;
  const t = Date.parse(until);
  if (Number.isNaN(t)) return true;
  return t > Date.now();
}

/** Sincroniza la lista de DeCA del backend + descarga sus PDFs a IndexedDB. */
export async function syncConductorDecas() {
  const { data } = await api.get("/api/v1/decas", { params: { limit: 500 } });
  const items = (data.items || []).filter(isPublicDownloadActive);
  const activeIds = new Set(items.map((d) => d.id));
  const db = await getDb();
  const txDec = db.transaction("decas", "readwrite");
  await txDec.store.clear();
  for (const d of items) await txDec.store.put(d);
  await txDec.done;

  const cachedPdfs = await db.getAll("pdfs");
  for (const p of cachedPdfs) {
    if (!activeIds.has(p.id)) await db.delete("pdfs", p.id);
  }

  for (const d of items) {
    const key = d.id;
    const existing = await db.get("pdfs", key);
    if (existing && existing.version === d.version) continue;
    try {
      const res = await fetch(`${API}/v1/decas/${d.id}/pdf`, {
        credentials: "include",
      });
      if (!res.ok) continue;
      const blob = await res.blob();
      await db.put("pdfs", { id: key, version: d.version, blob, cached_at: Date.now() });
    } catch (_) {
      /* offline mid-sync */
    }
  }
  await db.put("meta", { key: "last_sync", value: new Date().toISOString() });
  return items;
}

export async function getCachedDecas() {
  const db = await getDb();
  const all = await db.getAll("decas");
  return all.filter(isPublicDownloadActive);
}

export async function getCachedPdfBlob(decaId) {
  const db = await getDb();
  const entry = await db.get("pdfs", decaId);
  return entry?.blob || null;
}

export async function getLastSync() {
  const db = await getDb();
  const row = await db.get("meta", "last_sync");
  return row?.value || null;
}

export async function clearOfflineCache() {
  const db = await getDb();
  await db.clear("decas");
  await db.clear("pdfs");
  await db.delete("meta", "last_sync");
}
